import API_BASE from '../config';
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, Scale, Globe, ArrowUpRight,
  Loader2, X, Lock, CheckCircle2, AlertTriangle,
  Clock, ChevronRight
} from "lucide-react";
import axios from "axios";
import UpgradeGate from "../components/UpgradeGate";

// ─── Gap level styles ─────────────────────────────────────────────────────────

const gapColors: Record<string, string> = {
  CRITICAL:  "bg-red-50 text-red-700 border-red-200",
  HIGH:      "bg-orange-50 text-orange-700 border-orange-200",
  MEDIUM:    "bg-amber-50 text-amber-700 border-amber-200",
  LOW:       "bg-blue-50 text-blue-700 border-blue-200",
  COMPLIANT: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const gapIcon = (level: string) => {
  if (level === "COMPLIANT") return <CheckCircle2 size={12} />;
  if (level === "CRITICAL" || level === "HIGH") return <AlertTriangle size={12} />;
  return <Clock size={12} />;
};

const stagger = (i: number) => ({
  initial:    { opacity: 0, y: 12 },
  animate:    { opacity: 1, y: 0  },
  transition: { duration: 0.25, delay: i * 0.07 },
});

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Compliance() {
  const [activeModal,  setActiveModal]  = useState<any>(null);
  const [gapResult,    setGapResult]    = useState<any>(null);
  const [gapLoading,   setGapLoading]   = useState(false);
  const [showUpgrade,  setShowUpgrade]  = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["assessment"],
    queryFn:  () =>
      axios.get(`${API_BASE}/assessment/latest`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }).then((res) => res.data),
    refetchOnWindowFocus: true,
    enabled: !!localStorage.getItem("token"),
  });

  const { data: userData, isLoading: userLoading } = useQuery({
    queryKey: ["user-profile"],
    queryFn:  () =>
      axios.get(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }).then((res) => res.data),
    enabled: !!localStorage.getItem("token"),
  });

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
      <Loader2 size={36} className="animate-spin text-red-600" />
    </div>
  );

  const alerts            = data?.reg_alerts || [];
  const passingFrameworks = alerts.filter((a: any) => (a.compliance_score || 0) >= 70).length;
  const failingFrameworks = alerts.filter((a: any) => (a.compliance_score || 0) < 40).length;
  const warningFrameworks = alerts.filter((a: any) => {
    const s = a.compliance_score || 0; return s >= 40 && s < 70;
  }).length;

  const currency    = data?.form_data?.revenue_currency || "USD";
  const symbol = currency === "NGN" ? "₦" : currency === "GBP" ? "£" :
    currency === "EUR" ? "€" : currency === "ZAR" ? "R" :
    currency === "INR" ? "₹" : "$";

  const totalFineExposure = alerts.reduce((sum: number, a: any) => {
    return sum + (a.fine_usd_max || 0);
  }, 0);

  const complianceScore = alerts.length > 0
    ? Math.round((passingFrameworks / alerts.length) * 100)
    : 100;
  const tier         = userData?.subscription_tier?.toLowerCase() || "standard";
  const isProfessional = tier === "professional" || tier === "enterprise";
  const isEnterprise = tier === "enterprise";

  const regionLabels: Record<string, string> = {
    GBP: "United Kingdom", EUR: "European Union",  USD: "United States",
    NGN: "Nigeria",        AED: "United Arab Emirates", CAD: "Canada",
    AUD: "Australia",      ZAR: "South Africa",    INR: "India",
    SGD: "Singapore",      SAR: "Saudi Arabia",
  };
  // Use primary_country first (set in Stage 1), fall back to currency-derived
  const countryLabels: Record<string, string> = {
    nigeria:        "Nigeria",
    united_kingdom: "United Kingdom",
    united_states:  "United States",
    european_union: "European Union",
    canada:         "Canada",
    australia:      "Australia",
    uae:            "UAE",
    saudi_arabia:   "Saudi Arabia",
    india:          "India",
    singapore:      "Singapore",
    south_africa:   "South Africa",
    other:          "Global",
  };
  const primaryCountry = data?.form_data?.primary_country || "";
  const regionLabel = countryLabels[primaryCountry] || regionLabels[currency] || "Global";

  const handleGapAnalysis = async (reg: any) => {
    if (!isProfessional) { setShowUpgrade(true); return; }
    setActiveModal(reg);
    setGapResult(null);
    setGapLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/compliance/gap-analysis`,
        { regulation: reg },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setGapResult(res.data);
    } catch {
      setGapResult({ error: true });
    } finally {
      setGapLoading(false);
    }
  };

  const closeModal = () => {
    setActiveModal(null);
    setGapResult(null);
    setGapLoading(false);
    setShowUpgrade(false);
  };

  const downloadCompliancePDF = async () => {
    if (!gapResult || !activeModal) return;
    try {
      const res = await fetch(`${API_BASE}/api/compliance/intelligence/pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ gap_result: gapResult, regulation: activeModal }),
      });
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = "VicShield_Compliance_Intelligence.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("PDF download failed:", e);
    }
  };

  // Show loading spinner while user profile is fetching
  if (userLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f8fafc]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-red-600 border-t-transparent
            rounded-full animate-spin" />
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">
            Loading compliance data...
          </p>
        </div>
      </div>
    );
  }

  // Trial users see preview mode — real frameworks, locked gap analysis
  const isTrialPreview = tier === "standard";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 md:p-6 space-y-4 bg-[#f8fafc] min-h-screen font-['Inter'] text-left"
    >
      {/* Trial Preview Banner */}
      {isTrialPreview && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Clock size={16} className="text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-black text-amber-800 uppercase tracking-widest">Trial Preview Mode</p>
              <p className="text-[11px] text-amber-700 font-medium mt-0.5">
                You can see your applicable frameworks. Upgrade to Professional to unlock gap analysis and remediation actions.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowUpgrade(true)}
            className="flex-shrink-0 px-4 py-2 bg-amber-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-700 transition-all"
          >
            Upgrade →
          </button>
        </div>
      )}

      {/* ── HEADER ─────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
        <div>
          <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-600 mb-1">
            Regulatory Intelligence
          </p>
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 uppercase">
            Compliance Radar
          </h1>
          <p className="text-red-600 font-bold text-[10px] tracking-widest mt-1 uppercase">
            {regionLabel} Enforcement Risk Analysis
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-[#0f172a] text-white px-4 py-2.5 rounded-xl text-[10px]
            font-black uppercase tracking-widest border border-slate-700 shadow-sm">
            {currency} Zone
          </div>
          <div className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase
            tracking-widest border shadow-sm ${
            alerts.length > 0
              ? "bg-red-50 text-red-600 border-red-100"
              : "bg-emerald-50 text-emerald-600 border-emerald-100"
          }`}>
            {alerts.length} Active {alerts.length === 1 ? "Requirement" : "Requirements"}
          </div>
        </div>
      </div>

   {/* ── COMPLIANCE HERO ─────────────────────────────────────────── */}
      {alerts.length > 0 && (
        <div className="relative overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]
          rounded-[3rem] p-8 shadow-2xl border border-white/5">

          {/* Top stats row */}
          {/* Total exposure + compliance score */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-500 mb-2">
                Total Regulatory Fine Exposure
              </p>
              <p className="text-5xl font-black text-white tracking-tighter">
                {symbol}{totalFineExposure.toLocaleString()}
              </p>
              <p className="text-[10px] text-slate-400 font-medium mt-2">
                Combined maximum penalty across {alerts.length} active frameworks
              </p>
            </div>
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">
                Overall Compliance Score
              </p>
              <div className="flex items-baseline gap-2 mb-3">
                <span className={`text-5xl font-black tracking-tighter ${
                  complianceScore >= 70 ? "text-emerald-400" :
                  complianceScore >= 40 ? "text-amber-400" : "text-red-400"
                }`}>{complianceScore}%</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2">
                <div className={`h-2 rounded-full transition-all ${
                  complianceScore >= 70 ? "bg-emerald-500" :
                  complianceScore >= 40 ? "bg-amber-500" : "bg-red-500"
                }`} style={{ width: `${complianceScore}%` }} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
              <p className="text-3xl font-black text-white">{alerts.length}</p>
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mt-1">Active Frameworks</p>
            </div>
            <div className="bg-emerald-900/30 border border-emerald-800/30 rounded-2xl p-5 text-center">
              <p className="text-3xl font-black text-emerald-400">{passingFrameworks}</p>
              <p className="text-[9px] font-black uppercase tracking-widest text-emerald-500 mt-1">Compliant</p>
            </div>
            <div className="bg-amber-900/30 border border-amber-800/30 rounded-2xl p-5 text-center">
              <p className="text-3xl font-black text-amber-400">{warningFrameworks}</p>
              <p className="text-[9px] font-black uppercase tracking-widest text-amber-500 mt-1">Gaps Found</p>
            </div>
            <div className="bg-red-900/30 border border-red-800/30 rounded-2xl p-5 text-center">
              <p className="text-3xl font-black text-red-400">{failingFrameworks}</p>
              <p className="text-[9px] font-black uppercase tracking-widest text-red-500 mt-1">Non-Compliant</p>
            </div>
          </div>

          {/* Compliance table */}
          <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-white/5">
              <p className="col-span-5 text-[9px] font-black uppercase tracking-widest text-slate-500">Framework</p>
              <p className="col-span-2 text-[9px] font-black uppercase tracking-widest text-slate-500">Region</p>
              <p className="col-span-2 text-[9px] font-black uppercase tracking-widest text-slate-500">Urgency</p>
              <p className="col-span-2 text-[9px] font-black uppercase tracking-widest text-slate-500">Deadline</p>
              <p className="col-span-1 text-[9px] font-black uppercase tracking-widest text-slate-500">Action</p>
            </div>
            {alerts.slice(0, 6).map((reg: any, i: number) => (
              <div key={i} className={`grid grid-cols-12 px-6 py-4 items-center ${
                i < alerts.slice(0, 6).length - 1 ? "border-b border-white/5" : ""
              } hover:bg-white/5 transition-all cursor-pointer`}
                onClick={() => handleGapAnalysis(reg)}
              >
                <div className="col-span-5 flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    reg.urgency === "CRITICAL" ? "bg-red-500" :
                    reg.urgency === "HIGH" ? "bg-amber-500" : "bg-blue-500"
                  }`} />
                  <p className="text-xs font-black text-white truncate">{reg.name}</p>
                </div>
                <p className="col-span-2 text-[10px] font-bold text-slate-400">{reg.region}</p>
                <div className="col-span-2">
                  <span className={`text-[9px] font-black uppercase px-2 py-1 rounded-full ${
                    reg.urgency === "CRITICAL" ? "bg-red-900/50 text-red-400" :
                    reg.urgency === "HIGH" ? "bg-amber-900/50 text-amber-400" :
                    "bg-blue-900/50 text-blue-400"
                  }`}>{reg.urgency}</span>
                </div>
                <p className="col-span-2 text-[10px] font-bold text-slate-400">{reg.deadline_days} days</p>
                <div className="col-span-1">
                  <ChevronRight size={14} className="text-slate-500 hover:text-white transition-colors" />
                </div>
              </div>
            ))}
          </div>

          {alerts.length > 6 && (
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 text-center mt-4">
              +{alerts.length - 6} more frameworks — scroll down to view all
            </p>
          )}
        </div>
      )}

      {/* ── REGULATION CARDS ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {alerts.length > 0 ? (
          alerts.map((reg: any, i: number) => (
            <motion.div
              key={i}
              {...stagger(i)}
              className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-[2.5rem] border border-white/10 overflow-hidden
                flex flex-col hover:border-red-500/30 hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="p-8 flex-1">
                {/* Card top row */}
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${
                      reg.urgency === "CRITICAL"
                        ? "bg-red-900/40 text-red-400"
                        : "bg-white/10 text-slate-300"
                    }`}>
                      <Scale size={20} />
                    </div>
                                       <span className="font-black text-[9px] uppercase tracking-[0.2em] text-slate-300">
                      {reg.region} Standard
                    </span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
                    reg.urgency === "CRITICAL" ? "bg-red-600 text-white" :
                    reg.urgency === "HIGH"     ? "bg-red-100 text-red-600 border border-red-200" :
                    reg.urgency === "MEDIUM"   ? "bg-yellow-100 text-yellow-700 border border-yellow-200" :
                    "bg-blue-100 text-blue-600 border border-blue-200"
                  }`}>
                    {reg.urgency}
                  </span>
                </div>

                <h3 className="text-xl font-black text-white mb-2 uppercase tracking-tight">
                  {reg.name}
                </h3>
                <p className="text-slate-300 text-sm font-medium leading-relaxed mb-6">
                  {reg.description}
                </p>

                {/* Fine + deadline */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-white/5 rounded-2xl border border-white/10">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                      Fine Risk
                    </p>
                    <p className="text-xs font-black text-red-600 uppercase leading-tight">
                      {reg.fine_risk}
                    </p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-2xl border border-white/10">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                      Deadline
                    </p>
                    <p className="text-xs font-black text-white uppercase">
                      {reg.deadline_days ? `${reg.deadline_days} Days` : "Immediate"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action button */}
              <button
                onClick={() => handleGapAnalysis(reg)}
                className="w-full py-4 bg-[#0f172a] text-white text-[10px] font-black uppercase
                  tracking-widest flex items-center justify-center gap-2
                  group-hover:bg-red-600 transition-colors duration-200"
              >
                Generate Compliance Intelligence
                {isEnterprise
                  ? <ArrowUpRight size={15} />
                  : <Lock size={13} />
                }
              </button>
            </motion.div>
          ))
        ) : (
          <div className="col-span-2 p-20 text-center bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-[2.5rem]
            border-2 border-dashed border-white/10">
            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center
              justify-center mx-auto mb-5">
              <ShieldCheck size={32} className="text-emerald-500" />
            </div>
            <h3 className="text-base font-black text-slate-900 uppercase mb-1">
              Posture Synchronized
            </h3>
            <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">
              No {regionLabel} regulatory threats detected based on your current profile.
            </p>
          </div>
        )}
      </div>

      {/* ── COMPLIANCE HORIZON BANNER ────────────────────────────────── */}
      <motion.div {...stagger(alerts.length)}
        className="bg-[#0f172a] rounded-[2.5rem] p-10 relative overflow-hidden
          border-l-[8px] border-red-600 shadow-xl">
        <div className="relative z-10">
          <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-3">
            Automated Compliance Horizon
          </h2>
          <p className="text-slate-400 text-sm font-medium max-w-2xl leading-relaxed">
            VicShield Intelligence continuously monitors 356+ regulatory frameworks across
            34 countries and jurisdictions. Our engine triggers an alert the moment a legislative
            change affects your entity size, sector, or data residency in the{" "}
            <strong className="text-white">{regionLabel}</strong> zone.
          </p>
        </div>
        <Globe
          className="absolute top-1/2 right-[-40px] -translate-y-1/2 text-white opacity-[0.04]"
          size={320}
        />
      </motion.div>

      {/* ── UPGRADE MODAL ───────────────────────────────────────────── */}
      <AnimatePresence>
        {showUpgrade && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50
              flex items-center justify-center p-6"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0f172a] rounded-[3rem] p-10 max-w-2xl w-full
                border-t-8 border-red-600 shadow-2xl"
            >
              <div className="flex justify-between items-start mb-6">
                <div className="w-14 h-14 bg-red-600/10 rounded-2xl flex items-center
                  justify-center border border-red-600/20">
                  <Lock size={24} className="text-red-500" />
                </div>
                <button onClick={closeModal}
                  className="text-slate-500 hover:text-white transition-colors">
                  <X size={20} />
                </button>
              </div>

              <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-2">
                Unlock Compliance Intelligence
              </h2>
              <p className="text-slate-400 text-sm font-medium leading-relaxed mb-8">
                Compliance Intelligence is available on Professional and Enterprise plans.
                Choose the plan that fits your organisation.
              </p>
  
              {/* Three plan cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

                {/* Starter */}
                <div className="bg-white/5 border border-white/10 rounded-[2rem] p-6">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">
                    Starter
                  </p>
                  <p className="text-2xl font-black text-white mb-1">$199<span className="text-sm font-medium text-slate-400">/mo</span></p>
                  <p className="text-xs text-slate-500 mb-4">or $159/mo billed annually</p>
                  <div className="space-y-2 mb-6">
                    {[
                      "8-page starter audit report",
                      "Regulatory compliance radar",
                      "Financial exposure calculation",
                      "Domain threat monitoring",
                      "Basic vendor list",
                      "1 entity assessment",
                    ].map((f, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle2 size={12} className="text-slate-400 flex-shrink-0" />
                        <span className="text-slate-300 text-xs font-medium">{f}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={async () => {
                      const token = localStorage.getItem("token");
                      const res = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                        body: JSON.stringify({ tier: "starter", billing: "monthly" }),
                      });
                      const data = await res.json();
                      if (data.checkout_url) document.location.assign(data.checkout_url);
                    }}
                    className="w-full py-3 bg-slate-600 text-white rounded-xl font-black
                      text-[10px] uppercase tracking-widest hover:bg-slate-700 transition-all">
                    Upgrade to Starter
                  </button>
                </div>

                {/* Professional */}
                <div className="bg-white/5 border border-white/10 rounded-[2rem] p-6">
                  <p className="text-[9px] font-black uppercase tracking-widest text-blue-400 mb-1">
                    Professional
                  </p>
                  <p className="text-2xl font-black text-white mb-1">$499<span className="text-sm font-medium text-slate-400">/mo</span></p>
                  <p className="text-xs text-slate-500 mb-4">or $399/mo billed annually</p>
                  <div className="space-y-2 mb-6">
                    {[
                      "Compliance Intelligence gap analysis",
                      "Specific remediation per regulation",
                      "Compliance score with timeline",
                      "Monte Carlo confidence intervals",
                      "Full vendor risk matrix",
                      "Insurance intelligence",
                      "2 entities + 2 team members",
                      "API access + Audit trail",
                    ].map((f, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle2 size={12} className="text-blue-400 flex-shrink-0" />
                        <span className="text-slate-300 text-xs font-medium">{f}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={async () => {
                      const token = localStorage.getItem("token");
                      const res = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                        body: JSON.stringify({ tier: "professional", billing: "monthly" }),
                      });
                      const data = await res.json();
                      if (data.checkout_url) document.location.assign(data.checkout_url);
                    }}
                    className="w-full py-3 bg-blue-600 text-white rounded-xl font-black
                      text-[10px] uppercase tracking-widest hover:bg-blue-700 transition-all">
                    Upgrade to Professional
                  </button>
                </div>

                {/* Enterprise */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-[2rem] p-6">
                  <p className="text-[9px] font-black uppercase tracking-widest text-amber-400 mb-1">
                    Enterprise
                  </p>
                  <p className="text-2xl font-black text-white mb-1">$999<span className="text-sm font-medium text-slate-400">/mo</span></p>
                  <p className="text-xs text-slate-500 mb-4">or $799/mo billed annually</p>
                  <div className="space-y-2 mb-6">
                    {[
                      "Everything in Professional",
                      "18-slide Board Intelligence Deck",
                      "VicShield Compliance Intelligence (bespoke)",
                      "VicShield Insurance Intelligence",
                      "Sector benchmarking",
                      "5 entities + 10 team members",
                      "Full API access",
                      "Priority support",
                    ].map((f, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle2 size={12} className="text-amber-400 flex-shrink-0" />
                        <span className="text-slate-300 text-xs font-medium">{f}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={async () => {
                      const token = localStorage.getItem("token");
                      const res = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                        body: JSON.stringify({ tier: "enterprise", billing: "monthly" }),
                      });
                      const data = await res.json();
                      if (data.checkout_url) document.location.assign(data.checkout_url);
                    }}
                    className="w-full py-3 bg-amber-500 text-white rounded-xl font-black
                      text-[10px] uppercase tracking-widest hover:bg-amber-600 transition-all">
                    Upgrade to Enterprise
                  </button>
                </div>
              </div>

              <button onClick={closeModal}
                className="block w-full py-3 text-slate-500 font-black text-[10px]
                  uppercase tracking-widest text-center hover:text-white transition-colors">
                Maybe Later
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── GAP ANALYSIS MODAL ──────────────────────────────────────── */}
      <AnimatePresence>
        {activeModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50
              flex items-center justify-center p-6"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-[3rem] max-w-3xl w-full max-h-[90vh]
                overflow-y-auto shadow-2xl"
            >
              {/* Modal header */}
              <div className="sticky top-0 bg-[#0f172a] rounded-t-[3rem] p-9
                border-b-8 border-red-600 z-10">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-widest
                      text-slate-500 mb-1.5">
                      Compliance Intelligence — Enterprise
                    </p>
                    <h2 className="text-xl font-black text-white uppercase tracking-tight">
                      {activeModal.name}
                    </h2>
                    <p className="text-slate-500 text-[10px] font-bold mt-1 uppercase tracking-widest">
                      {activeModal.region} · {activeModal.deadline_days} Days · {activeModal.fine_risk}
                    </p>
                  </div>
                  <button onClick={closeModal}
                    className="text-slate-500 hover:text-white transition-colors ml-4 flex-shrink-0">
                    <X size={22} />
                  </button>
                </div>
              </div>

              {/* Modal body */}
              <div className="p-10">

                {/* Loading */}
                {gapLoading && (
                  <div className="flex flex-col items-center justify-center py-16">
                    <Loader2 size={40} className="animate-spin text-red-600 mb-5" />
                    <p className="text-slate-900 font-black uppercase tracking-widest text-sm">
                      Generating Compliance Intelligence
                    </p>
                    <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-2">
                      VicShield is analysing your audit data against {activeModal.name}...
                    </p>
                  </div>
                )}

                {/* Error */}
                {gapResult?.error && !gapLoading && (
                  <div className="text-center py-10">
                    <AlertTriangle size={36} className="mx-auto text-amber-500 mb-4" />
                    <p className="text-slate-900 font-black uppercase text-sm">
                      Analysis Unavailable
                    </p>
                    <p className="text-slate-400 text-[10px] font-bold mt-2 uppercase tracking-widest">
                      Please try again in a moment.
                    </p>
                  </div>
                )}

                {/* Results */}
                {gapResult && !gapResult.error && !gapLoading && (
                  <div className="space-y-7">

                    {/* Summary */}
                    <div className="bg-slate-50 rounded-2xl p-7 border-2 border-slate-100">
                      <p className="text-[9px] font-black uppercase tracking-widest
                        text-slate-400 mb-3">
                        Executive Summary
                      </p>
                      <p className="text-slate-700 font-medium leading-relaxed text-sm">
                        {gapResult.summary}
                      </p>
                    </div>

                    {/* Score + cost */}
                    <div className="grid grid-cols-2 gap-5">
                      <div className="bg-white rounded-2xl p-7 border-2 border-slate-100">
                        <p className="text-[9px] font-black uppercase tracking-widest
                          text-slate-400 mb-2">
                          Compliance Score
                        </p>
                        <div className="flex items-baseline gap-1">
                          <span className={`text-5xl font-black ${
                            gapResult.compliance_score >= 70 ? "text-emerald-600" :
                            gapResult.compliance_score >= 40 ? "text-amber-600" : "text-red-600"
                          }`}>
                            {gapResult.compliance_score}
                          </span>
                          <span className="text-2xl font-black text-slate-200">/100</span>
                        </div>
                      </div>
                      <div className="bg-white rounded-2xl p-7 border-2 border-slate-100">
                        <p className="text-[9px] font-black uppercase tracking-widest
                          text-slate-400 mb-2">
                          Est. Compliance Cost
                        </p>
                        <p className="text-base font-black text-slate-900">
                          {gapResult.estimated_compliance_cost}
                        </p>
                      </div>
                    </div>

                    {/* Requirements */}
                    <div>
                      <p className="text-[9px] font-black uppercase tracking-widest
                        text-slate-400 mb-4">
                        Requirements & Gaps
                      </p>
                      <div className="space-y-4">
                        {gapResult.requirements?.map((req: any, i: number) => (
                          <div key={i}
                            className="bg-white rounded-2xl border-2 border-slate-100 overflow-hidden">
                            <div className="p-6">
                              <div className="flex justify-between items-start mb-4">
                                <h4 className="text-sm font-black text-slate-900 uppercase
                                  tracking-tight flex-1 pr-4">
                                  {req.requirement}
                                </h4>
                                <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full
                                  text-[9px] font-black uppercase border flex-shrink-0
                                  ${gapColors[req.gap_level] || gapColors.MEDIUM}`}>
                                  {gapIcon(req.gap_level)}
                                  {req.gap_level}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                  <p className="text-[9px] font-black uppercase tracking-widest
                                    text-slate-400 mb-1">
                                    Required
                                  </p>
                                  <p className="text-xs font-bold text-slate-600 leading-relaxed">
                                    {req.mandate}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-[9px] font-black uppercase tracking-widest
                                    text-slate-400 mb-1">
                                    Your Status
                                  </p>
                                  <p className="text-xs font-bold text-slate-600 leading-relaxed">
                                    {req.current_status}
                                  </p>
                                </div>
                              </div>
                              {req.gap_level !== "COMPLIANT" && (
                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100
                                  flex items-start gap-3">
                                  <ChevronRight size={13} className="text-red-600 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <p className="text-[9px] font-black uppercase tracking-widest
                                      text-red-600 mb-1">
                                      Action — {req.timeline}
                                    </p>
                                    <p className="text-xs font-bold text-slate-700">{req.action}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Immediate actions */}
                    <div className="bg-red-50 rounded-2xl p-7 border border-red-100">
                      <p className="text-[9px] font-black uppercase tracking-widest
                        text-red-600 mb-4">
                        Immediate Actions Required
                      </p>
                      <div className="space-y-3">
                        {gapResult.immediate_actions?.map((action: string, i: number) => (
                          <div key={i} className="flex items-start gap-3">
                            <span className="w-5 h-5 bg-red-600 text-white rounded-full flex items-center
                              justify-center text-[9px] font-black flex-shrink-0 mt-0.5">
                              {i + 1}
                            </span>
                            <p className="text-sm font-bold text-slate-900">{action}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Risk if ignored */}
                    <div className="bg-[#0f172a] rounded-2xl p-7">
                      <p className="text-[9px] font-black uppercase tracking-widest
                        text-slate-500 mb-3">
                        Risk If Non-Compliance Continues
                      </p>
                      <p className="text-white font-bold text-sm leading-relaxed">
                        {gapResult.risk_if_ignored}
                      </p>
                    </div>

                    <p className="text-[9px] font-bold text-slate-300 uppercase tracking-widest text-center">
                      Generated by VicShield Intelligence Engine · Enterprise Confidential
                    </p>

                    {/* Download Button */}
                    <div className="flex justify-center pt-2">
                      <button
                        onClick={downloadCompliancePDF}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-white
                          text-slate-900 rounded-2xl text-[10px] font-black uppercase
                          tracking-widest hover:bg-slate-100 transition-all"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2.5">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                          <polyline points="7 10 12 15 17 10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        Download Report
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </motion.div>
  );
}
